import Element from './Element';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import mixin from '../privates/utils/mixin';
import * as members from '../privates/privateMembers';
import {validate} from '../privates/validations/validation';
import backgroundMediaUtils from '../privates/utils/backgroundMediaUtils';


function mergedCompDesign(compDesign, mediaData) {
    return Object.assign({}, compDesign, {
        type: 'MediaPlayerDesignData',
        background: Object.assign({}, compDesign.background, {
            mediaRef: mediaData,
            type: 'BackgroundMedia'
        })
    });
}

/**
 * @class VideoBox
 * @summary An element for displaying videos in customizable video containers.
 * @classdesc
 *  A [`VideoBox`](https://support.wix.com/en/article/videobox) is a design element that allows you to showcase your videos in designed, fully-customizable video containers. 
 *  A video box also allows you to control how site visitors interact with it. 
 * 
 *  Unlike the [`VideoPlayer`](https://www.wix.com/velo/reference/$w/videoplayer) element, a video box does 
 *  not expose text descriptions or titles, and cannot be displayed in full-screen mode.
 * 
 *  A video box allows you to present video from the following sources:
 *
 *  + Videos uploaded to your site
 *  + Transparent videos uploaded to your site
 *  + Videos and transparent videos from the Wix Media library
 * 
 * @mixes $w.Element
 * @mixes $w.HiddenCollapsedMixin
 * @memberof $w
 * @queryable
 * @tagname videoBox
 * @viewername wysiwyg.viewer.components.MediaPlayer
 * @definitionId wysiwyg.viewer.components.MediaPlayer
 */


/**
 * @function onMouseIn
 * @summary 
 *  **Note: This standard element property is not relevant for VideoBox.**
 * @description
 *  Adds an event handler that runs when the mouse pointer is moved
 *  onto the element.
 * 
 * > **Deprecation note:** The $w parameter of event handlers is being deprecated. To get
 *  a scoped selector for working with elements in repeater items, use the [$w.at()]($w.html#at) function
 *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`. To learn more, see
 *  [here](https://www.wix.com/velo/forum/wix-tips-and-updates/removal-of-the-w-parameter-from-event-handlers).
 * @param {$w.MouseEventHandler} handler The name of the function or
 *  the function expression to run when the mouse pointer is moved onto the
 *  element.
 * @returns {$w.Element} The element to which the event handler was added.
 * @override
 * @eventtype mouseenter
 * @memberof $w.VideoBox
 * @eventhandler
 */


/**
 * @function onMouseOut
 * @summary 
 *  **Note: This standard element property is not relevant for VideoBox.**
 * @description
 *  Adds an event handler that runs when the mouse pointer is moved
 *  off of the element.
 * 
 *  > **Deprecation note:** The $w parameter of event handlers is being deprecated. To get
 *  a scoped selector for working with elements in repeater items, use the [$w.at()]($w.html#at) function
 *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`. To learn more, see
 *  [here](https://www.wix.com/velo/forum/wix-tips-and-updates/removal-of-the-w-parameter-from-event-handlers).
 * @param {$w.MouseEventHandler} handler The name of the function or
 *  the function expression to run when the mouse pointer is moved off of
 *  the element.
 * @returns {$w.Element} The element to which the event handler was added.
 * @override
 * @eventtype mouseleave
 * @memberof $w.VideoBox
 * @eventhandler
 */

/**
 * @member isPlaying
 * @label property
 * @syntax
 * get isPlaying(): boolean
 * @summary Indicates if a video is currently playing.
 * @description
 *  To set the `isPlaying` property, use the [`play()`](#play),
 *  [`pause()`](#pause), and [`stop()`](#stop) functions.
 * @snippet [VideoBox-isPlaying_get.es6=Get whether a video is playing]
 * @type {Boolean}
 * @memberof $w.VideoBox
 * @instance
 */

class VideoBox extends Element {
    get isPlaying() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const {autoplay} = RMI.getProps(compId);
        const {isPlaying = autoplay} = RMI.getState(compId);
        
        return isPlaying;
    }
    

/**
 * @member currentTime
 * @label property
 * @syntax
 * get currentTime(): Number
 * @summary Gets the current play time from the beginning of the current video, in seconds.
 * @snippet [VideoBox-currentTime_get.es6=Get the current play time]
 * @type {Number}
 * @memberof $w.VideoBox
 * @instance
 */

    get currentTime() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const {currentTime = 0} = RMI.getState(compId);    
        
        return currentTime;
    }
    
  /**
     * @member duration
     * @label property
     * @syntax
     * get duration(): Number
     * @summary Gets the total play time of the current video, in seconds.
     * @snippet [VideoBox-duration_get.es6=Get the current video's duration]
     * @type {Number}
     * @memberof $w.VideoBox
     * @instance
     */

    get duration() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const {duration = 0} = RMI.getState(compId);
    
        return duration;
    }
    
    get volume() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const {volume = 1} = RMI.getState(compId);    
        
        return volume * 100;
    }
    

  /**
     * @member volume
     * @label property
     * @syntax
     * get volume(): Number
     * set volume(value: Number): void
     * @summary Sets or gets the video box volume.
     * @description
     *  Setting the `volume` property sets the video box's percentage of total
     *  volume. Set the value to a number between `0` and `100`.
     *
     *  Getting the `volume` property returns the video box's current
     *  percentage of total volume.
     * @note
     *  The actual volume heard by a site visitor depends on the value of the
     *  `volume` property and the volume settings on the visitor's device.
     * @snippet [VideoBox-volume_get.es6=Get a video box's volume]
     * @snippet [VideoBox-volume_set.es6=Set a video box's volume]
     * @type {Number}
     * @memberof $w.VideoBox
     * @instance
     */

    /* MARTA: This was here - is it necessary?
    * @member volume
    * @memberof $w.VideoBox
    * @param {external:Number} volume
    */
    set volume(volume) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compType = members._type.get(this).compType;
    
        if (!validate({
            compType,
            functionName: 'volume',
            dataToValidate: {volume},
            compName: this.id
        })) {
            return;
        }
    
        RMI.executeCompBehavior(compId, 'setVolume', {volume: volume / 100});
    }
    

  /**
     * @member isMuted
     * @label property
     * @syntax
     * get isMuted(): boolean
     * @summary Indicates if the volume is currently muted.
     * @description
     *  To set the `isMuted` property, use the [`mute()`](#mute)
     *  and [`unmute()`](#unmute) functions.
     * @snippet [VideoBox-isMuted_get.es6=Get whether the volume is muted]
     * @type {Boolean}
     * @memberof $w.VideoBox
     * @instance
     */

    get isMuted() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const {mute} = RMI.getProps(compId);
        const {isMuted = mute} = RMI.getState(compId);
    
        return isMuted;
    }

  /**
     * @function play
     * @syntax
     * function play(): VideoBox
     * @summary Begins or resumes playback.
     * @description
     *  The `play()` function plays the current video and fires a `play` event.
     * 
     *  The [onPlay( )](#onPlay) event handler is called.
     * 
     *  Any event handlers [set in the Editor](https://support.wix.com/en/article/wix-editor-about-videoboxes#change-the-behavior-of-your-videobox) are also called.
     * @snippet [VideoBox-play.es6=Begin or resume playback]
     * @snippet [VideoBox-play-consolelog.es6=Begin or resume playback and log a message when done]
     * @returns {Promise}
     * @fulfill {void} When the playback begins or resumes.
     * @memberof $w.VideoBox
     */

    play() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
    
        return new Promise((resolve) => {
            RMI.executeCompBehavior(compId, 'play', {}, () => resolve());
        });
    }
    

  /**
     * @function pause
     * @syntax
     * function pause(): VideoBox
     * @summary Pauses playback.
     * @description
     *  The `pause()` function pauses the current video and fires a `pause` event.
     *
     *  The [onPause( )](#onPause) event handler is called.
     * 
     *  Any event handlers [set in the Editor](https://support.wix.com/en/article/wix-editor-about-videoboxes#change-the-behavior-of-your-videobox) are also called.
     * @snippet [VideoBox-pause.es6=Pause playback]
     * @snippet [VideoBox-pause-consolelog.es6=Pause playback and log a message when done]
     * @returns {Promise}
     * @fulfill {void} When the playback has been paused.
     * @memberof $w.VideoBox
     */

    pause() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
    
        return new Promise((resolve) => {
            RMI.executeCompBehavior(compId, 'pause', {}, () => resolve());
        });
    }


  /**
     * @function stop
     * @syntax
     * function stop(): VideoBox
     * @summary Stops the playback.
     * @description
     *  The `stop()` function stops the current playback,
     *  moves to the beginning of the video, and fires a `pause` event.
     * 
     *  The [onPause( )](#onPause) event handler is called.
     * 
     *  Any event handlers [set in the Editor](https://support.wix.com/en/article/wix-editor-about-videoboxes#change-the-behavior-of-your-videobox) are also called.
     * @snippet [VideoBox-stop.es6=Stop playback]
     * @snippet [VideoBox-stop-consolelog.es6=Stop playback and log a message when done]
     * @returns {Promise}
     * @fulfill {void} When the playback has been stopped.
     * @memberof $w.VideoBox
     */

    stop() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
    
        return new Promise((resolve) => {
            RMI.executeCompBehavior(compId, 'stop', {}, () => resolve());
        });
    }


  /**
     * @function togglePlay
     * @syntax
     * function togglePlay(): VideoBox
     * @summary Toggles playback.
     * @description
     *  The `togglePlay()` function either pauses or plays the current video based
     *  on its current state:
     *
     *  + If the video is paused, it plays and a `play` event is fired. The
     *    `play` event handlers set on the video box by the [onPlay( )](#onPlay)
     *    function are called. (Any event handlers [set in the Editor](https://support.wix.com/en/article/wix-editor-about-videoboxes#change-the-behavior-of-your-videobox) are also called.)
     *  + If the video is playing, it is paused and a `pause` event is fired. The
     *    `pause` event handlers set on the video box by the [onPause( )](#onPause)
     *    function are called.  (Any event handlers [set in the Editor](https://support.wix.com/en/article/wix-editor-about-videoboxes#change-the-behavior-of-your-videobox) are also called.)
     * @snippet [VideoBox-togglePlay.es6=Toggle playback]
     * @snippet [VideoBox-togglePlay-consolelog.es6=Toggle playback and log a message when done]
     * @returns {Promise}
     * @fulfill {void} When the playback has been toggled.
     * @memberof $w.VideoBox
     */

    togglePlay() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
    
        return new Promise((resolve) => {
            RMI.executeCompBehavior(compId, 'togglePlay', {}, () => resolve());
        });
    }

  /**
     * @function mute
     * @syntax
     * function mute(): VideoBox
     * @summary Mutes video box volume.
     * @snippet [VideoBox-mute.es6=Mute volume]
     * @snippet [VideoBox-mute-consolelog.es6=Mute volume and log a message when done]
     * @returns {Promise}
     * @fulfill {void} When the volume has been muted.
     * @memberof $w.VideoBox
     */

    mute() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
    
        return new Promise((resolve) => {
            RMI.executeCompBehavior(compId, 'mute', {}, () => resolve());
        });
    }


  /**
     * @function unmute
     * @syntax
     * function unmute(): VideoBox
     * @summary Unmutes video volume.
     * @snippet [VideoBox-unmute.es6=Unmute volume]
     * @snippet [VideoBox-unmute-consolelog.es6=Unmute volume and log a message when done]
     * @returns {Promise}
     * @fulfill {void} When the volume has been unmuted.
     * @memberof $w.VideoBox
     */
    
    

    unmute() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
    
        return new Promise((resolve) => {
            RMI.executeCompBehavior(compId, 'unMute', {}, () => resolve());
        });
    }

    seek(time) {
        const RMI = members._RMI.get(this);
        const compType = members._type.get(this).compType;
        const compId = members._compId.get(this);

        if (!validate({
            compType,
            functionName: 'seek',
            dataToValidate: {seek: time},
            compName: this.id
        })) {
            return;
        }
        
        return new Promise((resolve) => {
            RMI.executeCompBehavior(compId, 'seek', {time}, () => resolve());
        });
    }
    
    /* MARTA: THIS WAS ORIGINALLY IN FILE, NOT SURE IF NECESSARY YET
     * 
     * @param cb 
     * @eventtype onPlay
     * @eventhandler
     */


  /**
     * @function onPlay
     * @syntax
     * function onPlay(handler: EventHandler): VideoBox
     * callback EventHandler(): void
     * @summary Adds an event handler that runs when the playback is started or restarted.
     * @description
     *  A video in a video box is played when a site visitor clicks the video box's
     *  play button or by calling its [`play()`](#play) function.
     * @snippet [VideoBox-onPlay.es6=Get the ID of the video box that is playing]
     * @param {$w.EventHandler} handler The name of the function or
     *  the function expression to run when the video in the video box is played.
     * @returns {$w.VideoBox} The video box that triggered the event.
     * @memberof $w.VideoBox
     * @see [play( )](#play)
     * @eventtype onPlay
     * @eventhandler
     */

    onPlay(cb) {
        const RMI = members._RMI.get(this);
        const compType = members._type.get(this).compType;

        if (!validate({
            compType,
            functionName: 'onPlay',
            dataToValidate: {onPlay: cb},
            compName: this.id
        })) {
            return this;
        }

        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);

        RMI.registerEvent(contextId, compId, RMI.getEventTypes().ON_PLAY, controllerId, cb);
        return this;
    }


  /**
     * @function onPause
     * @syntax
     * function onPause(handler: EventHandler): VideoBox
     * callback EventHandler(): void
     * @summary Adds an event handler that runs when the playback is paused.
     * @description
     *  A video in a video box is paused when a site visitor clicks the video box's
     *  pause button or by calling its [`pause()`](#pause) or [`stop()`](#stop) functions.
     * @snippet [VideoBox-onPause.es6=Get the ID of the video box that has been paused]
     * @param {$w.EventHandler} handler The name of the function or
     *  the function expression to run when the video is paused.
     * @returns {$w.VideoBox} The video box that triggered the event.
     * @memberof $w.VideoBox
     * @see [pause( )](#pause)
     * @eventtype onPause
     * @eventhandler
     */

    /**
     * 
     * @param cb 
     * @eventtype onPause
     * @eventhandler
     */
    onPause(cb) {
        const RMI = members._RMI.get(this);
        const compType = members._type.get(this).compType;

        if (!validate({
            compType,
            functionName: 'onPause',
            dataToValidate: {onPause: cb},
            compName: this.id
        })) {
            return this;
        }

        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);

        RMI.registerEvent(contextId, compId, RMI.getEventTypes().ON_PAUSE, controllerId, cb);
        return this;
    }


  /**
     * @function onEnded
     * @syntax
     * function onEnded(handler: EventHandler): VideoBox
     * callback EventHandler(): void
     * @summary Adds an event handler that runs when the playback has ended.
     * @description
     *  A video ends when the playback reaches the [`duration`](#duration) of the video.
     * 
     *  If the video is set to play in a loop, this event handler is never triggered.
     * @snippet [VideoBox-onEnded.es6=Get the ID of the video box whose video has ended]
     * @param {$w.EventHandler} handler The name of the function or
     *  the function expression to run when the video has ended.
     * @returns {$w.VideoBox} The video box that triggered the event.
     * @memberof $w.VideoBox
     * @eventtype onEnded
     * @eventhandler
     */

    /* Marta: Is the following necessary?
     * 
     * @param cb 
     * @eventtype onEnded
     * @eventhandler
     */
    onEnded(cb) {
        const RMI = members._RMI.get(this);
        const compType = members._type.get(this).compType;

        if (!validate({
            compType,
            functionName: 'onEnded',
            dataToValidate: {onEnded: cb},
            compName: this.id
        })) {
            return this;
        }

        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);

        RMI.registerEvent(contextId, compId, RMI.getEventTypes().ON_ENDED, controllerId, cb);
        return this;
    }

  /**
     * @function onProgress
     * @syntax
     * function onProgress(handler: EventHandler): VideoBox
     * callback EventHandler(): void
     * @summary Adds an event handler that runs when the playback progresses.
     * @description
     *  A video continually progresses as the video plays.
     * @snippet [VideoBox-onProgress.es6=Get the ID of the video box that is progressing]
     * @param {$w.EventHandler} handler The name of the function or
     *  the function expression to run when the video progresses.
     * @returns {$w.VideoBox}
     * @memberof $w.VideoBox
     * @eventtype onProgress
     * @eventhandler
     */


    /**
     * 
     * @param cb 
     * @eventtype onProgress
     * @eventhandler
     */
    onProgress(cb) {
        const RMI = members._RMI.get(this);
        const compType = members._type.get(this).compType;

        if (!validate({
            compType,
            functionName: 'onProgress',
            dataToValidate: {onProgress: cb},
            compName: this.id
        })) {
            return this;
        }

        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);

        RMI.registerEvent(contextId, compId, RMI.getEventTypes().ON_PROGRESS, controllerId, cb);
        return this;
    }
    


  /**
     * @member src
     * @label property
     * @syntax
     * get src(): string
     * set src(value: string): void
     * @summary Sets or gets the file location of the current video.
     * @description
     *  Setting the `src` property changes the displayed video to
     *  the video found at the new `src` value.
     *
     *  Getting the `src` property returns the location of the
     *  current video file.
     *
     *  The video file is a video from the <a href="https://support.wix.com/en/article/about-the-media-manager" target="_blank">Media Manager</a>.
     *  URL Format: `wix:video://v1/<video_uri>/<filename>#posterUri=<poster_uri>&posterWidth=<width>&posterHeight=<height>`. 
     * 
     *  Transparent videos are also supported.
     * @snippet [VideoBox-src_get.es6=Get the current video's location]
     * @snippet [VideoBox-src_set-wix.es6=Set the current video to a video uploaded to your site]
     * @type {String}
     * @memberof $w.VideoBox
     * @instance
     */


    get src() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compDesign = RMI.getDesign(compId) || {};
        const mediaRef = compDesign.background.mediaRef || {};

        return backgroundMediaUtils.buildFullUrlFromMediaRef(mediaRef);
    }

    set src(value) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'src',
            dataToValidate: {src: value},
            compName: this.id
        })) {
            return;
        }
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compDesign = RMI.getDesign(compId);
        const currentMediaData = compDesign && compDesign.background.mediaRef;
        const mediaData = backgroundMediaUtils.getMediaDataFromSrc(value);

        // Silently fail
        if (!mediaData || !compDesign) {
            return;
        }
        if (mediaData.videoId === currentMediaData.videoId) {
            return;
        }

        // !!temp hack - partial design data is getting an unwanted merge
        mediaData.qualities = null;
        // Preserve opacity
        const {opacity = 1} = currentMediaData;
        mediaData.opacity = opacity;
        // setDesignAsync with full media data
        backgroundMediaUtils.getFullMediaData(mediaData, (fullMediaData) => {
            if (!fullMediaData){
                return;
            }
            const updatedCompDesign = RMI.getDesign(compId);
            // Preserve opacity 
            fullMediaData.opacity = updatedCompDesign.background.mediaRef.opacity;
            // set mediaFeatures to null if not present, runtimeDAL always merge and we need it not to be undefined
            fullMediaData.mediaFeatures = fullMediaData.mediaFeatures || null;
            //Replace mediaData completely
            updatedCompDesign.background.mediaRef = fullMediaData;
            RMI.setDesign(compId, updatedCompDesign);
        });

       
        // Set props to preserve playstate
        RMI.setProps(compId, {
            autoplay: this.isPlaying,
            mute: this.isMuted,
            volume: this.volume
        });
       
        // Merge and set partial media data
        RMI.setDesign(compId, mergedCompDesign(compDesign, mediaData));
       
    }

    toJSON() {
        const {id, type, global, rendered, isPlaying, currentTime, duration, volume, isMuted} = this;
        return {id, type, global, rendered, isPlaying, currentTime, duration, volume, isMuted};
    }
}
mixin(VideoBox.prototype, HiddenCollapsedMixin.prototype);

export default VideoBox;

